<template>
  <div class="page-main flex-page">
    <!--START: Header Wrapper-->
    <div
      class="header-wrapper"
      :class="{ 'show-shadow': showOptions.headerShadow }"
    >
      <!--START: Page Header-->
      <RegularHeader
        class="regular-header"
        :data="regularHeader"
      ></RegularHeader>
      <!--END: Page Header-->

      <!--Manage Licenses Button (disabled if not subscribed)-->
      <button
        class="btn btn-small btn-primary"
        :disabled="!isSubscribed"
        @click="openLicenseManager"
      >
        Manage Licenses
      </button>
    </div>
    <!--END: Header Wrapper-->

    <!--START: Loader-->
    <PageLoader
      v-if="lodash.isEmpty(company) || showOptions.pageLoader"
      :showLoader="true"
    ></PageLoader>
    <!--END: Loader-->

    <div v-else ref="scrollWrapper" class="scroll-main" @scroll="contentScroll">
      <!--START: Licensed Agents List-->
      <div v-if="licensedAgents.length > 0" class="agents-wrapper">
        <ul class="grid-table">
          <li
            class="grid-row grid-header"
            :class="{ 'grid-header-shadow': showOptions.headerShadow }"
          >
            <span class="grid-column">Agent</span>
            <span class="grid-column">Language</span>
            <span class="grid-column">Auto-Translate</span>
            <span class="grid-column">Actions</span>
          </li>
        </ul>
        <ul
          class="grid-table grid-table-body"
          ref="table"
          @scroll="tableScroll"
        >
          <li
            v-for="(agent, index) in licensedAgents"
            :key="index"
            class="grid-row"
          >
            <div class="grid-column agent-column">
              <h3 class="name">{{ agent.name }}</h3>
              <span class="email">{{ agent.email }}</span>
            </div>

            <div class="grid-column">
              <span class="language-value">{{ getAgentLanguage(agent) }}</span>
            </div>

            <div class="grid-column">
              <span
                class="table-tag"
                :class="getAgentAutoTranslate(agent) ? 'green' : 'grey'"
              >
                {{ getAgentAutoTranslate(agent) ? "Enabled" : "Disabled" }}
              </span>
            </div>

            <div class="grid-column">
              <button
                class="btn btn-small btn-border"
                @click="openAgentSettings(agent)"
              >
                Configure
              </button>
            </div>
          </li>
        </ul>

        <!--Agent Count-->
        <span class="grid-count"
          >{{ licensedAgents.length }}
          {{ licensedAgents.length == 1 ? "agent" : "agents" }}</span
        >
      </div>
      <!--END: Licensed Agents List-->

      <!--START: Zero State-->
      <ZeroState
        v-else
        class="zero-state"
        :imageURL="zeroStateContent.imageURL"
        :title="zeroStateContent.title"
        :message="zeroStateContent.message"
        :buttonTitle="zeroStateContent.buttonTitle"
        @eventHandler="goToSubscriptions"
      ></ZeroState>
      <!--END: Zero State-->
    </div>

    <!--START: Agent Settings Modal-->
    <div v-if="showOptions.agentSettings" class="modal show">
      <ModalHeader
        :title="`Configure ${selectedAgent?.name}`"
        @closeModal="closeAgentSettings"
      ></ModalHeader>

      <div class="modal-body">
        <FormBuilder
          :fields="agentSettingsFields"
          class="settings-form"
        ></FormBuilder>

        <div class="modal-actions">
          <button class="btn btn-primary" @click="saveAgentSettings">
            Save Settings
          </button>
          <button class="btn btn-border" @click="closeAgentSettings">
            Cancel
          </button>
        </div>
      </div>
    </div>
    <div
      class="bg-overlay light"
      :class="{ show: showOptions.agentSettings }"
      @click="closeAgentSettings"
    ></div>
    <!--END: Agent Settings Modal-->

    <!--START: Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>
    <!--END: Loader-->

    <!--START: Status Message-->
    <StatusMessage
      :show="status.show"
      :status="status.status"
      :title="status.title"
    ></StatusMessage>
    <!--END: Status Message-->

    <!--START: License Manager Modal-->
    <LicenseManager
      v-if="showOptions.licenseManager"
      :subscription="subscription"
      @updateLicenses="updateLicenses"
      @closeModal="closeLicenseManager"
    ></LicenseManager>
    <div
      class="bg-overlay"
      :class="{ show: showOptions.licenseManager }"
      @click="closeLicenseManager"
    ></div>
    <!--END: License Manager Modal-->
  </div>
</template>

<script>
// Import libraries
import _ from "lodash";

// Importing components
import RegularHeader from "@/components/dashboard/RegularHeader";
import ZeroState from "@/components/misc/ZeroState";
import ModalHeader from "@/components/modals/ModalHeader";
import FormBuilder from "@/components/form/FormBuilder";
import LineLoader from "@/components/loaders/LineLoader";
import PageLoader from "@/components/loaders/PageLoader";
import StatusMessage from "@/components/modals/StatusMessage";
import LicenseManager from "@/components/dashboard/subscriptions/AiAssistant/LicenseManager";

// Importing Services
import { AiAssistantServices } from "@/services";
const { AgentService } = AiAssistantServices;

// Languages list (must match widget/assets/scripts/main.js)
const LANGUAGES = [
  "Arabic",
  "Chinese (Simplified)",
  "Chinese (Traditional)",
  "Czech",
  "Danish",
  "Dutch",
  "English",
  "Estonian",
  "Filipino",
  "Finnish",
  "French",
  "German",
  "Greek",
  "Hebrew",
  "Hindi",
  "Hungarian",
  "Indonesian",
  "Italian",
  "Japanese",
  "Korean",
  "Latvian",
  "Lithuanian",
  "Malay",
  "Norwegian",
  "Polish",
  "Portuguese",
  "Romanian",
  "Russian",
  "Spanish",
  "Swahili",
  "Swedish",
  "Thai",
  "Turkish",
  "Vietnamese",
];

export default {
  name: "Agents",
  data() {
    return {
      lodash: _,
      showOptions: {
        headerShadow: false,
        lineLoader: false,
        pageLoader: true,
        agentSettings: false,
        licenseManager: false,
      },
      regularHeader: {
        title: "Agent Settings",
        description: "Configure settings for your licensed agents",
      },
      licensedAgents: [],
      agentConfigs: {},
      selectedAgent: null,
      agentSettingsFields: {
        language: {
          type: "dropdown",
          title: "Preferred Language",
          placeholder: "Select language",
          required: false,
          hasError: false,
          value: ["English", "English"],
          fields: LANGUAGES.map((lang) => ({ name: lang, value: lang })),
        },
        autoTranslateTicket: {
          type: "toggle",
          title: "Auto-translate tickets",
          required: false,
          hasError: false,
          value: false,
        },
      },
      status: {
        show: false,
        status: "success",
        title: "",
      },
    };
  },
  props: {},
  watch: {},
  components: {
    RegularHeader,
    ZeroState,
    ModalHeader,
    FormBuilder,
    LineLoader,
    PageLoader,
    StatusMessage,
    LicenseManager,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    subscription() {
      return this.company?.products?.aiAssistant?.subscription;
    },
    isSubscribed() {
      return (
        this.subscription?.planType === "standard" &&
        this.subscription?.status === "active"
      );
    },
    zeroStateContent() {
      // Show different message based on subscription status
      if (!this.isSubscribed) {
        return {
          imageURL: "services.jpg",
          title: "Subscription required",
          message: "You can add licenses once you subscribe to a plan",
          buttonTitle: "Go to Subscriptions",
        };
      }
      return {
        imageURL: "services.jpg",
        title: "No licensed agents",
        message: "Assign licenses to agents to configure their settings",
        buttonTitle: "Manage Licenses",
      };
    },
  },
  async created() {
    await this.loadAgents();
  },
  methods: {
    // Load licensed agents and their configurations
    async loadAgents() {
      this.showOptions.pageLoader = true;

      // Get licensed users from subscription
      const licensedUsers = this.subscription?.licenses?.users || [];

      if (licensedUsers.length === 0) {
        this.licensedAgents = [];
        this.showOptions.pageLoader = false;
        return;
      }

      // Fetch agent configurations from backend
      const zendeskUserIds = licensedUsers.map((u) => u.zendeskUserId);
      const response = await AgentService.GetAllAgents({ zendeskUserIds });

      if (!response.hasError) {
        // Build a map of agent configs by zendeskUserId
        this.agentConfigs = {};
        (response.data || []).forEach((agent) => {
          this.agentConfigs[agent.zendeskUserId] = agent;
        });
      }

      this.licensedAgents = licensedUsers;
      this.showOptions.pageLoader = false;
    },

    // Get agent language from config (default: English)
    getAgentLanguage(agent) {
      const config = this.agentConfigs[agent.zendeskUserId];
      return config?.settings?.language || "English";
    },

    // Get agent auto-translate setting
    getAgentAutoTranslate(agent) {
      const config = this.agentConfigs[agent.zendeskUserId];
      return config?.settings?.autoTranslateTicket || false;
    },

    // Open agent settings modal
    openAgentSettings(agent) {
      this.selectedAgent = agent;
      const config = this.agentConfigs[agent.zendeskUserId];

      // Populate form with current settings (default language: English)
      const language = config?.settings?.language || "English";
      this.agentSettingsFields.language.value = [language, language];
      this.agentSettingsFields.autoTranslateTicket.value =
        config?.settings?.autoTranslateTicket || false;

      this.showOptions.agentSettings = true;
    },

    // Close agent settings modal
    closeAgentSettings() {
      this.showOptions.agentSettings = false;
      this.selectedAgent = null;
    },

    // Save agent settings
    async saveAgentSettings() {
      if (!this.selectedAgent) return;

      this.showOptions.lineLoader = true;

      // Dropdown value is [name, value] array, get the value (index 1)
      const languageValue = this.agentSettingsFields.language.value;
      const settings = {
        language: Array.isArray(languageValue)
          ? languageValue[1]
          : languageValue,
        autoTranslateTicket: this.agentSettingsFields.autoTranslateTicket.value,
      };

      const response = await AgentService.UpdateAgentSettings({
        zendeskUserId: this.selectedAgent.zendeskUserId,
        settings,
        agentData: {
          zendeskUserId: this.selectedAgent.zendeskUserId,
          name: this.selectedAgent.name,
          email: this.selectedAgent.email,
          role: this.selectedAgent.role,
        },
      });

      this.showOptions.lineLoader = false;

      if (!response.hasError) {
        // Update local config
        this.agentConfigs[this.selectedAgent.zendeskUserId] = response.data;
        this.closeAgentSettings();
        this.status.status = "success";
        this.status.title = "Agent settings saved";
      } else {
        this.status.status = "error";
        this.status.title = response.message || "Failed to save settings";
      }

      this.showStatusMessage(this.status, 3000);
    },

    // Navigate to subscriptions page or open license manager
    goToSubscriptions() {
      if (this.isSubscribed) {
        this.openLicenseManager();
      } else {
        this.$router.push("/settings/subscriptions");
      }
    },

    // Open license manager modal
    openLicenseManager() {
      this.showOptions.licenseManager = true;
    },

    // Close license manager modal
    closeLicenseManager() {
      this.showOptions.licenseManager = false;
    },

    // Update licenses from license manager
    async updateLicenses(agents) {
      this.showOptions.lineLoader = true;

      const response = await AgentService.UpdateLicenses({
        agents: agents.map((a) => ({
          zendeskUserId: a.zendeskUserId,
          name: a.name,
          email: a.email,
          role: a.role,
        })),
      });

      this.showOptions.lineLoader = false;

      if (!response.hasError) {
        // Update the store with new company data
        this.$store.dispatch("setCompany", response.data);
        this.closeLicenseManager();

        // Reload agents list
        await this.loadAgents();

        this.status.status = "success";
        this.status.title = "Licenses updated";
      } else {
        this.status.status = "error";
        this.status.title = response.message || "Failed to update licenses";
      }

      this.showStatusMessage(this.status, 3000);
    },

    // Event on scroll
    contentScroll() {
      const scrollTop = this.$refs.scrollWrapper.scrollTop;

      if (scrollTop > 0) this.showOptions.headerShadow = true;
      else this.showOptions.headerShadow = false;
    },

    // Event on table scroll
    tableScroll() {
      // Handle table scroll if needed
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.header-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9e6ef;
  margin-bottom: 0 !important;
  z-index: 2;

  .regular-header {
    flex: 1;
    max-width: 70%;
    margin-right: 30%;
  }
}

.scroll-main {
  flex: 1;
  padding: 2rem 1.5rem;
  overflow-y: scroll;
}

.agents-wrapper {
  .grid-column {
    &:nth-child(1) {
      width: 35%;
    }

    &:nth-child(2),
    &:nth-child(3) {
      width: 20%;
      text-align: center;
    }

    &:nth-child(4) {
      width: 25%;
      text-align: right;
    }
  }
}

.agent-column {
  .name {
    font-size: $smallFontSize;
    font-weight: $mediumFontWeight;
  }

  .email {
    font-size: $smallerFontSize;
    color: $greyColor;
  }
}

.language-value {
  font-size: $smallFontSize;
  color: $paragraphColor;
}

.table-tag.grey {
  background-color: darken($whiteColor, 10%);
  color: $greyColor;
  border: 1px solid darken($whiteColor, 15%);
}

.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: $whiteColor;
  border-radius: 1rem;
  min-width: 30rem;
  max-width: 40rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.15);

  .modal-body {
    padding: 1.5rem;
  }

  .modal-actions {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    margin-top: 1.5rem;
    justify-content: flex-end;
  }
}

.bg-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  &.show {
    opacity: 1;
    visibility: visible;
  }

  &.light {
    background: rgba(0, 0, 0, 0.3);
  }
}

.show-shadow {
  box-shadow: 0 0.5rem 0.7rem -0.5rem $boxShadowColor;
}

/deep/ .line-loader {
  position: fixed;
  z-index: 101;
  bottom: 0;
  left: 0;
}
</style>
